// Book Import — upload a broker's book, map its columns, validate, import.
//
// The screen is deliberately a one-way sequence: choose a product and a file,
// confirm what each column means, VALIDATE, and only then import. The Import
// button does not light up until a validation has actually run against the
// server, because the whole failure mode this replaces is a batch that looked
// fine and wasn't.
//
// Nothing here decides anything about the data. Every value shown — what is
// ready, what is a duplicate, why a row is blocked — comes from the server
// running the same pipeline it will run on import. The screen renders that
// answer; it does not compute a second opinion.
//
// Reasons arrive as { code, params, text }: the server names WHAT is wrong and
// with which values, and this screen composes the sentence in the reader's
// language. `text` is the same thing in English, kept for API callers, scripts
// and the audit log, and used here only if a code has no key — which would be
// a bundle bug rather than a design choice.
import React, { useState, useRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  S, Icon, PageHeader, BtnPrimary, DataCard, EmptyState, formatMoney,
} from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';
const AX = { withCredentials: true };

// Product labels reuse the keys the New Offer panel already has, so the two
// screens cannot drift into naming the same product differently.
const PRODUCTS = [
  { code: 'PGMA', key: 'offers.newOffer.lobPgma' },
  { code: 'RC_PRO', key: 'offers.newOffer.lobRcPro' },
  { code: 'PGM', key: 'offers.newOffer.lobPgm' },
];

const STATUS_STYLE = {
  ready: { color: S.success, key: 'sReady' },
  created: { color: S.success, key: 'sCreated' },
  duplicate: { color: S.info, key: 'sDuplicate' },
  blocked: { color: S.warning, key: 'sBlocked' },
  failed: { color: S.danger, key: 'sFailed' },
};

export default function BookImportScreen() {
  const { t } = useTranslation();
  const B = (k, o) => t(`offers.bookImport.${k}`, o);
  // The server sends labelKey/helpKey alongside the English label. Translate
  // the key; fall back to the label only when the key is genuinely missing,
  // which is a bundle bug rather than a design choice.
  const FL = (f) => (f.labelKey && t(f.labelKey) !== f.labelKey ? t(f.labelKey) : f.label);
  const FH = (f) => (f.helpKey && t(f.helpKey) !== f.helpKey ? t(f.helpKey) : f.help);
  // A reason is { code, params, text }. Older payloads (and anything this
  // screen has not taught itself to name yet) fall back to the English text
  // rather than rendering a raw code at the reader.
  const RT = (x) => {
    if (typeof x === 'string') return x;
    if (!x?.code) return x?.text || '';
    const key = `offers.bookImport.r.${x.code}`;
    const out = t(key, x.params || {});
    return out === key ? (x.text || x.code) : out;
  };
  const fileRef = useRef(null);

  const [product, setProduct] = useState('PGMA');
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);

  const [inspect, setInspect] = useState(null);      // server's read of the file
  const [fieldSpec, setFieldSpec] = useState(null);  // target fields for the product
  const [mapping, setMapping] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [groupByClient, setGroupByClient] = useState(false);
  const [saveAs, setSaveAs] = useState('');

  const [validation, setValidation] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState(null);
  const [openRow, setOpenRow] = useState(null);

  const errText = (e, fallback) =>
    e?.response?.data?.message
    || (e?.response?.data?.problems || []).join(' ')
    || e?.message
    || fallback;

  // Changing the product invalidates everything downstream of it.
  useEffect(() => {
    setInspect(null); setMapping([]); setValidation(null); setOutcome(null); setError(null);
    let cancelled = false;
    axios.get(`${BASE_URL}/offers/import/fields`, { ...AX, params: { product } })
      .then(r => { if (!cancelled) setFieldSpec(r.data.data); })
      .catch(e => { if (!cancelled) setError(errText(e, B('errFields'))); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  // Stable identity: `fieldSpec?.fields || []` allocates a new array on every
  // render, which would make the `missing` useMemo below recompute each time.
  const fields = useMemo(() => fieldSpec?.fields || [], [fieldSpec]);
  const mapIndex = useMemo(
    () => new Map(mapping.filter(m => m.source).map(m => [m.target, m.source])),
    [mapping],
  );

  // Mirrors the server's validateMapping so the Validate button can say what
  // is missing before a round trip. The server checks again regardless — this
  // is a courtesy, never the gate.
  const missing = useMemo(() => {
    if (!fields.length) return [];
    const out = [];
    for (const f of fields) {
      if (!f.required) continue;
      const hasDefault = f.defaultable && defaults[defaultKey(f.target)];
      if (!mapIndex.has(f.target) && !hasDefault) out.push(FL(f));
    }
    if (product === 'RC_PRO'
      && !mapIndex.has('policyholder.companyName') && !mapIndex.has('policyholder.lastName')) {
      out.push(`${B('f.policyholder_companyName')} / ${B('f.policyholder_lastName')}`);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, mapIndex, defaults, product, t]);

  const pickFile = (f) => {
    if (!f) return;
    setFile(f); setInspect(null); setValidation(null); setOutcome(null); setError(null);
    const form = new FormData();
    form.append('file', f);
    form.append('product', product);
    setBusy('inspect');
    axios.post(`${BASE_URL}/offers/import/inspect`, form, AX)
      .then(r => {
        const d = r.data.data;
        setInspect(d);
        setMapping(d.suggestedMapping || []);
      })
      .catch(e => setError(errText(e, B('errRead'))))
      .finally(() => setBusy(''));
  };

  const send = (dryRun) => {
    const form = new FormData();
    form.append('file', file);
    form.append('product', product);
    form.append('mapping', JSON.stringify(mapping));
    form.append('defaults', JSON.stringify(defaults));
    form.append('dryRun', String(dryRun));
    form.append('groupByClient', String(groupByClient));
    if (inspect?.meta?.sheet) form.append('sheet', inspect.meta.sheet);
    if (!dryRun && saveAs.trim()) form.append('saveMappingAs', saveAs.trim());

    setBusy(dryRun ? 'validate' : 'import');
    setError(null);
    // A 500-row import creates 500 offers one at a time through the product's
    // own endpoint. Ten minutes, matching the batch-document export.
    axios.post(`${BASE_URL}/offers/import`, form, { ...AX, timeout: 600000 })
      .then(r => {
        if (dryRun) { setValidation(r.data.data); setOutcome(null); } else setOutcome(r.data.data);
      })
      .catch(e => setError(errText(e, dryRun ? B('errValidate') : B('errImport'))))
      .finally(() => setBusy(''));
  };

  const applySaved = (m) => {
    setMapping(m.fields.map(f => ({ target: f.target, source: f.source })));
    setDefaults(m.defaults || {});
    setValidation(null); setOutcome(null);
  };

  const report = outcome || validation;

  return (
    <div style={{ padding: 24, maxWidth: 1180, margin: '0 auto' }}>
      <PageHeader
        // A fragment with an explicit separator, not an array: contiguous text
        // nodes collapse into ONE anonymous flex item, so the header's gap:6
        // never applies and the crumbs render as "OpérationsBook Import".
        breadcrumb={(
          <>
            <span>{B('breadcrumb')}</span>
            <Icon name='chevronRight' size={10} />
            <span>{B('title')}</span>
          </>
        )}
        title={B('title')}
      />

      <p style={{ color: S.textSecondary, marginTop: 16, marginBottom: 20, maxWidth: 760 }}>
        {B('intro')}
      </p>

      {error && (
        <div style={{
          background: '#fef2f2', border: `1px solid ${S.danger}`, color: '#991b1b',
          padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
        }}>{error}</div>
      )}

      {/* ── 1. product + file ─────────────────────────────── */}
      <DataCard style={{ marginBottom: 16 }}>
        <div style={{ padding: 18 }}>
          <Step n={1} title={B('step1')} />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '14px 0' }}>
            {PRODUCTS.map(p => (
              <button
                key={p.code}
                onClick={() => setProduct(p.code)}
                style={{
                  padding: '8px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                  border: `1px solid ${product === p.code ? S.brandPrimary : S.borderLight}`,
                  background: product === p.code ? S.brandPrimary : '#fff',
                  color: product === p.code ? '#fff' : S.textPrimary,
                }}
              >{t(p.key)}</button>
            ))}
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); pickFile(e.dataTransfer.files?.[0]); }}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${dragging ? S.brandPrimary : S.borderLight}`,
              background: dragging ? '#fdf2f4' : S.bgCard,
              borderRadius: 10, padding: '28px 20px', textAlign: 'center', cursor: 'pointer',
            }}
          >
            <Icon name='upload' color={S.textMuted} size={24} />
            <div style={{ marginTop: 8, fontSize: 14, color: S.textPrimary }}>
              {file ? file.name : B('drop')}
            </div>
            <div style={{ marginTop: 4, fontSize: 12, color: S.textMuted }}>
              {B('limits', { rows: fieldSpec?.maxRows || 500 })}
            </div>
            <input
              ref={fileRef} type='file' accept='.csv,.xlsx,.xlsm,.txt'
              style={{ display: 'none' }}
              onChange={(e) => pickFile(e.target.files?.[0])}
            />
          </div>

          {busy === 'inspect' && (
            <div style={{ marginTop: 10, fontSize: 13, color: S.textMuted }}>{B('reading')}</div>
          )}

          {inspect && (
            <div style={{ marginTop: 14, fontSize: 13, color: S.textSecondary }}>
              <strong style={{ color: S.textPrimary }}>{B('rowsN', { count: inspect.rowCount })}</strong>
              {' · '}{B('columnsN', { count: inspect.headers.length })}
              {inspect.meta?.encoding && <> · {B('encoding')} <code>{inspect.meta.encoding}</code></>}
              {inspect.meta?.delimiter && <> · {B('delimiter')} <code>{inspect.meta.delimiter}</code></>}
              {inspect.meta?.sheet && <> · {B('sheet')} <code>{inspect.meta.sheet}</code></>}
              {inspect.overCap && (
                <div style={{ color: S.danger, marginTop: 6 }}>
                  {B('overCap', { max: inspect.maxRows })}
                </div>
              )}
            </div>
          )}
        </div>
      </DataCard>

      {/* ── 2. mapping ────────────────────────────────────── */}
      {inspect && (
        <DataCard style={{ marginBottom: 16 }}>
          <div style={{ padding: 18 }}>
            <Step n={2} title={B('step2')} />

            {!!inspect.savedMappings?.length && (
              <div style={{ margin: '12px 0', fontSize: 13 }}>
                <span style={{ color: S.textMuted, marginRight: 8 }}>{B('savedMappings')}</span>
                {inspect.savedMappings.map(m => (
                  <button key={m._id} onClick={() => applySaved(m)} style={chipStyle}>{m.name}</button>
                ))}
              </div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
              <thead>
                <tr>
                  <Th style={{ width: '32%' }}>{B('colTarget')}</Th>
                  <Th style={{ width: '34%' }}>{B('colSource')}</Th>
                  <Th>{B('colDefault')}</Th>
                </tr>
              </thead>
              <tbody>
                {fields.map(f => {
                  const row = mapping.find(m => m.target === f.target) || { target: f.target, source: '' };
                  const dk = defaultKey(f.target);
                  const unmet = f.required && !row.source && !defaults[dk];
                  return (
                    <tr key={f.target} style={{ borderTop: `1px solid ${S.borderLight}` }}>
                      <td style={{ padding: '8px 10px', fontSize: 13 }}>
                        <div style={{
                          color: unmet ? S.danger : S.textPrimary,
                          fontWeight: f.required ? 600 : 400,
                        }}>
                          {FL(f)}{f.required && <span style={{ color: S.danger }}> *</span>}
                        </div>
                        {FH(f) && (
                          <div style={{ fontSize: 11, color: S.textMuted, marginTop: 2 }}>{FH(f)}</div>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <select
                          value={row.source}
                          onChange={(e) => {
                            const src = e.target.value;
                            setMapping(prev => {
                              const next = prev.filter(m => m.target !== f.target);
                              next.push({ target: f.target, source: src });
                              return next;
                            });
                            setValidation(null); setOutcome(null);
                          }}
                          style={selectStyle(unmet)}
                        >
                          <option value=''>{B('notInFile')}</option>
                          {inspect.headers.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        {f.defaultable ? (
                          <input
                            value={defaults[dk] ?? ''}
                            placeholder={placeholderFor(f.target)}
                            onChange={(e) => {
                              setDefaults(d => ({ ...d, [dk]: e.target.value }));
                              setValidation(null); setOutcome(null);
                            }}
                            style={selectStyle(false)}
                          />
                        ) : <span style={{ fontSize: 12, color: S.textMuted }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {product === 'RC_PRO' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 13 }}>
                <input
                  type='checkbox' checked={groupByClient}
                  onChange={(e) => { setGroupByClient(e.target.checked); setValidation(null); setOutcome(null); }}
                />
                <span>
                  {B('groupRoster')}
                  <span style={{ color: S.textMuted }}> {B('groupRosterHint')}</span>
                </span>
              </label>
            )}

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 18 }}>
              <BtnPrimary
                onClick={() => send(true)}
                disabled={busy !== '' || missing.length > 0 || inspect.overCap}
              >
                {busy === 'validate' ? B('validating') : B('validate')}
              </BtnPrimary>
              {missing.length > 0 && (
                <span style={{ fontSize: 12, color: S.danger }}>
                  {B('mapMissing', { fields: missing.join(', ') })}
                </span>
              )}
            </div>
          </div>
        </DataCard>
      )}

      {/* ── 3. result ─────────────────────────────────────── */}
      {report && (
        <DataCard>
          <div style={{ padding: 18 }}>
            <Step n={3} title={outcome ? B('importedTitle') : B('reportTitle')} />

            <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', margin: '14px 0 18px' }}>
              <Stat label={B('statRows')} value={report.rowsRead} />
              {outcome
                ? <Stat label={B('statCreated')} value={report.summary.created} color={S.success} />
                : <Stat label={B('statReady')} value={report.summary.ready} color={S.success} />}
              <Stat label={B('statDuplicate')} value={report.summary.duplicate} color={S.info} />
              <Stat label={B('statBlocked')} value={report.summary.blocked} color={S.warning} />
              {report.summary.failed > 0
                && <Stat label={B('statFailed')} value={report.summary.failed} color={S.danger} />}
              {report.summary.premium > 0 && (
                <Stat label={B('statPremium')} value={formatMoney(report.summary.premium)} />
              )}
            </div>

            {!outcome && (
              <div style={{
                display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
                padding: '12px 14px', background: S.bgCard, borderRadius: 8, marginBottom: 16,
              }}>
                <input
                  value={saveAs} onChange={(e) => setSaveAs(e.target.value)}
                  placeholder={B('saveAs')}
                  style={{ ...selectStyle(false), maxWidth: 380 }}
                />
                <BtnPrimary onClick={() => send(false)} disabled={busy !== '' || report.summary.ready === 0}>
                  {busy === 'import' ? B('importing') : B('importBtn', { count: report.summary.ready })}
                </BtnPrimary>
                <span style={{ fontSize: 12, color: S.textMuted }}>{B('skipNote')}</span>
              </div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <Th style={{ width: 90 }}>{B('thLine')}</Th>
                  <Th style={{ width: 130 }}>{B('thStatus')}</Th>
                  <Th>{B('thClient')}</Th>
                  <Th style={{ width: 150 }}>{B('thQuote')}</Th>
                </tr>
              </thead>
              <tbody>
                {report.results.map((r, i) => {
                  const st = STATUS_STYLE[r.status] || {};
                  const open = openRow === i;
                  const detail = [...(r.reasons || []), ...(r.notes || [])];
                  return (
                    <React.Fragment key={`${r.lines.join('-')}-${i}`}>
                      <tr
                        onClick={() => setOpenRow(open ? null : i)}
                        style={{
                          borderTop: `1px solid ${S.borderLight}`,
                          cursor: detail.length ? 'pointer' : 'default',
                        }}
                      >
                        <td style={td}>{r.lines.join(', ')}</td>
                        <td style={td}>
                          <span style={{ color: st.color, fontWeight: 600, fontSize: 12 }}>
                            {st.key ? B(st.key) : r.status}
                          </span>
                          {detail.length > 0 && (
                            <span style={{ color: S.textMuted, marginLeft: 6, fontSize: 11 }}>
                              {open ? '▾' : '▸'} {detail.length}
                            </span>
                          )}
                        </td>
                        <td style={td}>{r.client}</td>
                        <td style={{ ...td, color: S.textSecondary }}>
                          {r.offerNumber || (r.premium ? formatMoney(r.premium) : '—')}
                        </td>
                      </tr>
                      {open && detail.length > 0 && (
                        <tr>
                          <td colSpan={4} style={{ padding: '4px 14px 12px 14px', background: S.bgCard }}>
                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: S.textSecondary }}>
                              {detail.map((d, j) => (
                                <li key={j} style={{ marginTop: 3 }}>{RT(d)}</li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </DataCard>
      )}

      {!inspect && !busy && !error && (
        <EmptyState icon='upload' title={B('emptyTitle')} description={B('emptyBody')} />
      )}
    </div>
  );
}

// ─── small presentational helpers ────────────────────────────
const td = { padding: '8px 10px', fontSize: 13, color: S.textPrimary };

const chipStyle = {
  border: `1px solid ${S.borderLight}`, background: '#fff', borderRadius: 999,
  padding: '4px 12px', marginRight: 6, fontSize: 12, cursor: 'pointer', color: S.textPrimary,
};

const selectStyle = (bad) => ({
  width: '100%', padding: '6px 8px', fontSize: 13, borderRadius: 6,
  border: `1px solid ${bad ? S.danger : S.borderLight}`, background: '#fff', color: S.textPrimary,
});

const Th = ({ children, style }) => (
  <th style={{
    textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase',
    letterSpacing: '.04em', color: S.textMuted, fontWeight: 600, ...style,
  }}>{children}</th>
);

const Step = ({ n, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <span style={{
      width: 22, height: 22, borderRadius: 999, background: S.brandPrimary, color: '#fff',
      fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>{n}</span>
    <span style={{ fontSize: 15, fontWeight: 600, color: S.textPrimary }}>{title}</span>
  </div>
);

const Stat = ({ label, value, color }) => (
  <div>
    <div style={{ fontSize: 20, fontWeight: 700, color: color || S.textPrimary }}>{value}</div>
    <div style={{
      fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: S.textMuted,
    }}>{label}</div>
  </div>
);

// Mirrors DEFAULT_KEYS in services/bookImport/index.js.
function defaultKey(target) {
  return ({
    brokerName: 'brokerName',
    'productConfig.effectiveDate': 'effectiveDate',
    'productConfig.coverageType': 'coverageType',
    'productConfig.sumInsured': 'sumInsured',
    'productConfig.waitingPeriod': 'waitingPeriod',
    'productConfig.coveragePercent': 'coveragePercent',
  })[target] || target;
}

function placeholderFor(target) {
  return ({
    brokerName: 'Argos Prévoyance',
    'productConfig.effectiveDate': '2027-01-01',
    'productConfig.coverageType': 'maladie_accident',
    'productConfig.sumInsured': '10000000',
    'productConfig.waitingPeriod': '30',
    'productConfig.coveragePercent': '80',
  })[target] || '';
}
